import React, { useCallback, useState } from 'react';
import { Upload, File, X, Shield, Clock, Image as ImageIcon, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';

const FileUpload = ({ onFilesUploaded, disabled }) => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const supportedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/bmp', 'image/tiff', 'image/webp'];
      const files = Array.from(e.dataTransfer.files).filter(file => 
        supportedTypes.includes(file.type) || file.name.toLowerCase().endsWith('.pdf')
      );
      setSelectedFiles(files);
    }
  }, []);

  const handleFileSelect = useCallback((e) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setSelectedFiles(files);
    }
  }, []);

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = () => {
    if (selectedFiles.length > 0 && onFilesUploaded) {
      onFilesUploaded(selectedFiles);
    }
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith('image/') || file.name.toLowerCase().match(/\.(jpg|jpeg|png|bmp|tiff|webp)$/)) {
      return <ImageIcon className="w-5 h-5 text-purple-600" />;
    }
    return <File className="w-5 h-5 text-red-600" />;
  };

  const getFileIconBg = (file) => {
    if (file.type.startsWith('image/') || file.name.toLowerCase().match(/\.(jpg|jpeg|png|bmp|tiff|webp)$/)) {
      return 'bg-purple-100';
    }
    return 'bg-red-100';
  };

  return (
    <div className="space-y-4">
      {/* Privacy Notice */}
      <div className="bg-gradient-to-r from-emerald-50 via-green-50 to-teal-50 border-2 border-emerald-300 rounded-xl p-4 mb-4 shadow-sm hover:shadow-md transition-all duration-300">
        <div className="flex items-start space-x-3">
          <div className="bg-emerald-100 p-2 rounded-lg">
            <Shield className="w-5 h-5 text-emerald-600 flex-shrink-0" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-emerald-900 mb-1 flex items-center gap-1">
              🔒 100% Privacy-First Processing
            </p>
            <p className="text-xs text-emerald-700 leading-relaxed">
              Your files are processed <strong>securely</strong> - no third-party AI services. 
              All files <strong>auto-delete in 24h</strong> or delete manually after download.
            </p>
          </div>
          <Clock className="w-5 h-5 text-emerald-600 flex-shrink-0 animate-pulse" />
        </div>
      </div>

      <div
        data-testid="file-upload-dropzone"
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label="Upload PDF files or images"
        className={`relative border-3 border-dashed rounded-2xl p-10 transition-all duration-300 ${
          dragActive
            ? 'border-emerald-500 bg-emerald-50 scale-105 shadow-xl'
            : 'border-slate-300 bg-gradient-to-br from-slate-50 to-white hover:border-emerald-400 hover:bg-emerald-50/30 hover:shadow-lg'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !disabled && document.getElementById('file-input').click()}
        onKeyDown={(e) => {
          if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            document.getElementById('file-input').click();
          }
        }}
      >
        <input
          id="file-input"
          type="file"
          multiple
          accept=".pdf,.jpg,.jpeg,.png,.bmp,.tiff,.webp,image/*"
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled}
        />

        <div className="flex flex-col items-center justify-center space-y-5">
          <div className="flex items-center space-x-4">
            <div className={`w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg transition-all duration-300 ${dragActive ? 'scale-110 rotate-6' : 'hover:scale-105'}`}>
              <Upload className="w-10 h-10 text-white" />
            </div>
            <div className="text-5xl text-emerald-300 font-bold">+</div>
            <div className={`w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center shadow-lg transition-all duration-300 ${dragActive ? 'scale-110 -rotate-6' : 'hover:scale-105'}`}>
              <ImageIcon className="w-10 h-10 text-white" />
            </div>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-slate-900 mb-2">
              {dragActive ? '📥 Drop files here!' : 'Drop PDFs or Images here'}
            </p>
            <p className="text-sm text-slate-600 mb-1">
              or <span className="text-emerald-600 font-semibold underline">click to browse</span>
            </p>
            <p className="text-xs text-slate-500">
              PDF, JPG, PNG, BMP, TIFF, WEBP • Batch upload • Max 50 files
            </p>
            <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-100 to-pink-100 rounded-full border border-purple-300">
              <ImageIcon className="w-4 h-4 text-purple-600" />
              <span className="text-xs text-purple-700 font-bold">
                📸 Photo & Screenshot Support!
              </span>
            </div>
          </div>
        </div>

        {/* Animated border effect */}
        {dragActive && (
          <div className="absolute inset-0 rounded-2xl border-4 border-emerald-400 animate-pulse pointer-events-none"></div>
        )}
      </div>

      {/* Selected Files */}
      {selectedFiles.length > 0 && (
        <div className="space-y-3 animate-fadeIn">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-slate-800 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
              Selected Files ({selectedFiles.length})
            </p>
            <span className="text-xs text-slate-500">
              Total: {(selectedFiles.reduce((sum, f) => sum + f.size, 0) / 1024 / 1024).toFixed(2)} MB
            </span>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                data-testid={`selected-file-${index}`}
                className="flex items-center justify-between bg-white border-2 border-slate-200 rounded-xl p-3 hover:shadow-md hover:border-emerald-300 transition-all duration-200 group animate-slideInRight"
                style={{animationDelay: `${index * 50}ms`}}
              >
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <div className={`w-12 h-12 ${getFileIconBg(file)} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-200`}>
                    {getFileIcon(file)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-900 truncate">{file.name}</p>
                    <p className="text-xs text-slate-500">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                      {file.type && <span className="ml-1">• {file.type.split('/')[1]?.toUpperCase() || 'FILE'}</span>}
                    </p>
                  </div>
                </div>
                <button
                  data-testid={`remove-file-${index}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(index);
                  }}
                  className="p-2 hover:bg-red-50 rounded-lg transition-all duration-200 group/btn ml-2"
                  disabled={disabled}
                  aria-label={`Remove ${file.name}`}
                >
                  <X className="w-5 h-5 text-slate-400 group-hover/btn:text-red-600 transition-colors" />
                </button>
              </div>
            ))}
          </div>
          <Button
            data-testid="upload-files-button"
            onClick={handleUpload}
            disabled={disabled}
            className="w-full bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 hover:from-emerald-700 hover:via-teal-700 hover:to-cyan-700 text-white py-4 rounded-xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Upload className="w-5 h-5 mr-2 inline" />
            Upload & Process {selectedFiles.length} File{selectedFiles.length > 1 ? 's' : ''}
          </Button>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
